import { useEffect, useRef, useState } from "react";

function calculateVolumePercentage(average: number): number {
  const normalizedAverage = Number.isFinite(average) ? Math.max(0, average) : 0;
  return Math.min(100, (normalizedAverage / 255) * 100 * 2);
}

interface UseMicrophoneStatusOptions {
  subscribeVolume?: boolean;
  volumeUpdateThreshold?: number;
}

export function useMicrophoneStatus(stream: MediaStream | null, options?: UseMicrophoneStatusOptions) {
  const subscribeVolume = options?.subscribeVolume ?? true;
  const volumeUpdateThreshold = options?.volumeUpdateThreshold ?? 2;
  const [isActive, setIsActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0);
  const [volumePercentage, setVolumePercentage] = useState(0);

  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const muteSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!stream) {
      setIsActive(false);
      setIsMuted(false);
      setVolume(0);
      setVolumePercentage(0);
      return;
    }

    const audioTrack = stream.getAudioTracks()[0];

    if (!audioTrack) return;

    setIsActive(audioTrack.readyState === "live");
    const syncMutedFromTrack = () => setIsMuted(!audioTrack.enabled);
    syncMutedFromTrack();

    const handleEnded = () => setIsActive(false);
    const handleMute = () => setIsMuted(true);
    const handleUnmute = () => setIsMuted(false);
    audioTrack.addEventListener("ended", handleEnded);
    audioTrack.addEventListener("mute", handleMute);
    audioTrack.addEventListener("unmute", handleUnmute);

    // track.enabled changes don't emit mute/unmute events in common user-mute flows.
    muteSyncIntervalRef.current = setInterval(syncMutedFromTrack, 150);

    if (!subscribeVolume) {
      return () => {
        audioTrack.removeEventListener("ended", handleEnded);
        audioTrack.removeEventListener("mute", handleMute);
        audioTrack.removeEventListener("unmute", handleUnmute);
        if (muteSyncIntervalRef.current) {
          clearInterval(muteSyncIntervalRef.current);
          muteSyncIntervalRef.current = null;
        }
      };
    }

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();

    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;

    const data = new Uint8Array(analyser.frequencyBinCount);

    const updateVolume = () => {
      analyser.getByteFrequencyData(data);

      // Match useMicrophone behavior: average frequency-bin intensity.
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        sum += data[i];
      }
      const average = sum / data.length;
      const volumePercentValue = Math.round(calculateVolumePercentage(average));
      setVolumePercentage((prev) => (Math.abs(prev - volumePercentValue) >= volumeUpdateThreshold ? volumePercentValue : prev));

      setVolume(average);

      animationRef.current = requestAnimationFrame(updateVolume);
    };

    updateVolume();

    return () => {
      audioTrack.removeEventListener("ended", handleEnded);
      audioTrack.removeEventListener("mute", handleMute);
      audioTrack.removeEventListener("unmute", handleUnmute);

      if (muteSyncIntervalRef.current) {
        clearInterval(muteSyncIntervalRef.current);
        muteSyncIntervalRef.current = null;
      }

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      if (audioContext.state !== "closed") {
        audioContext.close();
      }
    };
  }, [stream, subscribeVolume, volumeUpdateThreshold]);

  return { isActive, isMuted, volume, volumePercentage };
}
